import React, {
  useCallback,
  useState,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import GalleryComponent from '@fuww/library/src/Lookbook/Gallery';
import Checkbox from '@fuww/library/src/Checkbox';
import debounce from 'lodash/debounce';
import { UserContext } from '../../UserProvider';
import List from '../Image/List/List';
import LoadMore from '../../LoadMore';
import loadMore from '../../../lib/queries/loadMore';
import searchTimeout from '../../../lib/lookbook/searchTimeout';
import withQuery from '../../../lib/withQuery';
import replaceRoute from '../../../lib/replaceRoute';
import loading from '../../../lib/loading';
import FullScreenLoading from '../../../lib/fullScreenLoading';
import imagesQuery from '../../../lib/queries/lookbook/images.graphql';
import variables from '../../../lib/queries/lookbook/variables/images';
import { saveRouter } from '../../../lib/lookbook/previousListRoute';
import {
  lookbookBrowserImageConfigurations,
} from '../../../lib/imageConfigurations';

const Uploader = dynamic(() => import(
  '../Button/Uploader'
), {
  ssr: false,
  loading,
});

const Tags = [
  'SS26',
  'FW25',
  'SS25',
  'Class25',
  'Denim',
  'Menswear',
  'Womenswear',
  'Bag',
  'Shoe',
  'Hat',
];

const updateSearch = (
  search,
  categoryId,
  categorySlug,
  internalUseOnly,
  route,
) => {
  const searchParameter = search || null;
  const parameters = {
    search: searchParameter,
    ...(internalUseOnly ? { internalUseOnly } : {}),
  };

  replaceRoute(
    route,
    {
      ...(categoryId ? { categoryId, slug: categorySlug } : {}),
      ...parameters,
    },
  );
};

const debouncedUpdateSearch = debounce(
  (search, categoryId, categorySlug, internalUseOnly, route) => {
    updateSearch(search, categoryId, categorySlug, internalUseOnly, route);
  },
  searchTimeout,
);

export const Gallery = ({
  data,
  fetchMore,
  loading: galleryLoading,
  variables: { search: searchVariable, categoryId },
  categorySlug,
  internalUseOnly,
  searchLabel,
  route,
  refetch,
  withUploader,
  preloadFirst,
}) => {
  const { user, roles } = useContext(UserContext);
  const [search, setSearch] = useState(searchVariable || '');
  const {
    images: {
      edges = [],
      pageInfo: { hasNextPage = false } = {},
    } = {},
  } = data;

  const label = `Search in ${searchLabel ? `${searchLabel}` : 'the lookbook'}`;

  const onSearch = useCallback(({ target: { value } }) => {
    setSearch(value);
    debouncedUpdateSearch(
      value,
      categoryId,
      categorySlug,
      internalUseOnly,
      route,
    );
  }, [categoryId, categorySlug, internalUseOnly, route]);

  return (
    <>
      <GalleryComponent
        tags={Tags}
        search={search}
        loading={galleryLoading}
        label={label}
        onSearch={onSearch}
        searchIsEmpty={edges.length === 0}
        onClear={
          () => {
            setSearch('');
            updateSearch(
              null,
              categoryId,
              categorySlug,
              internalUseOnly,
              route,
            );
          }
        }
        getUpdateSearch={
          (filter) => () => {
            setSearch(filter);
            updateSearch(
              filter,
              categoryId,
              categorySlug,
              internalUseOnly,
              route,
            );
          }
        }
        internalUseFilter={
          // eslint-disable-next-line no-underscore-dangle
          user && roles?.__global_roles__?.includes('internalImages')
            && (
            <Checkbox
              checked={internalUseOnly === 'true'}
              label="Internal Use images only"
              onChange={({ target: { checked } }) => {
                updateSearch(
                  search,
                  categoryId,
                  categorySlug,
                  checked,
                  route,
                );
              }}
            />
            )
        }
      >
        <List
          edges={edges}
          route={route}
          preloadFirst={preloadFirst}
        />
        <LoadMore
          rootMargin="1920px"
          hasNextPage={hasNextPage}
          loading={galleryLoading}
          fetchMore={() => loadMore({
            variables: { search: searchVariable },
          })(data, 'images', fetchMore)}
        />
      </GalleryComponent>
      { withUploader && (
        <Uploader
          currentUser={user}
          loaderIcon
          refetch={refetch}
        />
      )}
    </>
  );
};

Gallery.propTypes = {
  data: PropTypes.shape({
    images: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({})),
      pageInfo: PropTypes.shape({
        hasNextPage: PropTypes.bool,
      }),
    }),
  }),
  error: PropTypes.shape({}),
  loading: PropTypes.bool,
  variables: PropTypes.shape({
    search: PropTypes.string,
    categoryId: PropTypes.number,
  }).isRequired,
  fetchMore: PropTypes.func.isRequired,
  refetch: PropTypes.func,
  searchLabel: PropTypes.string,
  categorySlug: PropTypes.string,
  internalUseOnly: PropTypes.string,
  route: PropTypes.string,
  withUploader: PropTypes.bool.isRequired,
  preloadFirst: PropTypes.bool,
};

Gallery.defaultProps = {
  data: {},
  error: null,
  loading: false,
  refetch: undefined,
  searchLabel: null,
  categorySlug: null,
  internalUseOnly: null,
  route: 'lookbook',
  preloadFirst: false,
};

const GalleryWithQuery = withQuery(Gallery, { renderAlways: true });

const LookbookGallery = ({
  withUploader,
  ...properties
}) => {
  const router = useRouter();
  saveRouter(router);
  const { query: { search, categoryId, internalUseOnly } } = router;

  return (
    <GalleryWithQuery
      query={imagesQuery}
      showLoaderWhenSetVariables={false}
      variables={{
        ...variables,
        search: search || null,
        categoryId: Number(categoryId),
        internalUseOnly: internalUseOnly === 'true',
        imageConfigurations: lookbookBrowserImageConfigurations,
      }}
      internalUseOnly={internalUseOnly}
      loader={<FullScreenLoading />}
      errorMessage="Error loading lookbook images"
      withUploader={withUploader}
      preloadFirst
      {...properties}
    />
  );
};

LookbookGallery.propTypes = {
  withUploader: PropTypes.bool,
};

LookbookGallery.defaultProps = {
  withUploader: false,
};

export default LookbookGallery;
